import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Users, Plus, Trash2, TrendingUp, Target, Wallet, RotateCcw } from "lucide-react";
import { getColaboradoresMaoDeObra } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { DEPARTAMENTOS_CUSTO_MAO_DE_OBRA } from "../../constants/departamentosOrcamento";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const HORAS_PADRAO = 160;
// PIS Cumulativo 3% + COFINS Cumulativo 0,65% + ISS Prestador 3% — espelha
// backend/services/dre_gerencial_svc.ALIQUOTAS_IMPOSTOS_VENDA (Deduções de Vendas da DRE).
const IMPOSTOS_PADRAO_PCT = 6.65;
const STORAGE_KEY = "simulacaoSquads:v1";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  destaque: "#9D174D",
  destaqueBg: isLight ? "#FCE7F3" : "#3B1230",
  linhaBg: isLight ? "#F5F0FC" : "#1E1435",
  inputBg: isLight ? "#FFFFFF" : "#120B24",
  success: "#10B981",
  danger: "#EF4444",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

const ESTADO_INICIAL = {
  horas: HORAS_PADRAO,
  impostosPct: IMPOSTOS_PADRAO_PCT,
  linhas: [{ uid: 1, colaboradorId: null }],
  porFaturamento: { valorNf: 0, outros: 0, viagem: 0, freelancer: 0, rateios: 0 },
  metaFaturamento: { mbPct: 60, outros: 0, viagem: 0, freelancer: 0, rateios: 0 },
  custoDesejado: { valorNf: 0, mbPct: 60 },
};

// Simulação é rascunho pessoal (nada vai para o banco): o squad e os valores digitados ficam
// só no navegador de quem simula, para não se perderem ao recarregar a página.
function carregarEstadoSalvo() {
  try {
    const salvo = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!salvo || !Array.isArray(salvo.linhas)) return ESTADO_INICIAL;
    return {
      ...ESTADO_INICIAL,
      ...salvo,
      porFaturamento: { ...ESTADO_INICIAL.porFaturamento, ...salvo.porFaturamento },
      metaFaturamento: { ...ESTADO_INICIAL.metaFaturamento, ...salvo.metaFaturamento },
      custoDesejado: { ...ESTADO_INICIAL.custoDesejado, ...salvo.custoDesejado },
    };
  } catch {
    return ESTADO_INICIAL;
  }
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function rotuloColaborador(c) {
  return `${c.ocupante || "Vaga em aberto"} · ${c.cargo || "sem cargo"}`;
}

function CampoNumero({ C, valor, onChange, sufixo, prefixo, largura = 120 }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end",
      border: `1.5px solid ${C.destaque}`, borderRadius: 6, padding: "3px 8px", background: C.inputBg, width: largura,
    }}>
      {prefixo && <span style={{ fontSize: 12, color: C.muted }}>{prefixo}</span>}
      <input
        type="number"
        step="any"
        value={valor}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        style={{
          width: "100%", border: "none", outline: "none", background: "transparent",
          textAlign: "right", fontSize: 13, fontWeight: 600, color: C.text, fontFamily: "inherit",
        }}
      />
      {sufixo && <span style={{ fontSize: 12, color: C.muted }}>{sufixo}</span>}
    </div>
  );
}

// Uma linha do quadro de simulação. `input` (opcional) troca o valor calculado por um campo editável.
function Linha({ C, label, valor, input, negrito, sutil, faixa, destaque, corValor }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      padding: "6px 12px", minHeight: 34,
      background: destaque ? C.destaqueBg : faixa ? C.linhaBg : "transparent",
      borderTop: destaque ? `1px solid ${C.border}` : "none",
    }}>
      <span style={{
        fontSize: sutil ? 11.5 : 12.5, fontWeight: negrito || destaque ? 700 : 500,
        color: destaque ? C.destaque : C.text,
      }}>
        {label}
      </span>
      {input || (
        <span style={{
          fontSize: destaque ? 14 : 12.5, fontWeight: negrito || destaque ? 800 : 600,
          color: corValor || (destaque ? C.destaque : C.text), fontVariantNumeric: "tabular-nums",
        }}>
          {valor}
        </span>
      )}
    </div>
  );
}

function QuadroSimulacao({ C, isLight, icon: Icon, titulo, subtitulo, children }) {
  return (
    <section style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden",
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ padding: "14px 14px 10px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={15} color={C.destaque} />
          <h2 style={{ margin: 0, fontSize: 13.5, fontWeight: 800, color: C.destaque }}>{titulo}</h2>
        </div>
        <p style={{ margin: "4px 0 0", fontSize: 11, color: C.muted }}>{subtitulo}</p>
      </div>
      <div style={{ padding: "6px 0" }}>{children}</div>
    </section>
  );
}

// Simulação de Squads: monta um squad com colaboradores da Mão de Obra (Operação) e usa o custo
// mensal deles (salário + encargos + benefícios do mês escolhido) em três simuladores — margem
// bruta a partir do faturamento, meta de faturamento a partir da margem desejada e custo máximo a
// partir de faturamento + margem. Mesma lógica da DRE Gerencial: Receita Líquida = NF − deduções,
// Margem Bruta = Lucro Bruto ÷ Receita Líquida.
export default function SimulacaoSquadsView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL + 1);
  const [mes, setMes] = useState(1);
  const [colaboradores, setColaboradores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [estado, setEstado] = useState(carregarEstadoSalvo);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
    } catch {
      // armazenamento indisponível (aba anônima/bloqueado) — a simulação segue só em memória
    }
  }, [estado]);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro("");
    getColaboradoresMaoDeObra(ano)
      .then((lista) => {
        if (cancelado) return;
        const daOperacao = (lista || []).filter((c) => DEPARTAMENTOS_CUSTO_MAO_DE_OBRA.includes(c.departamento));
        daOperacao.sort((a, b) => rotuloColaborador(a).localeCompare(rotuloColaborador(b), "pt-BR"));
        setColaboradores(daOperacao);
      })
      .catch((e) => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [ano]);

  const colaboradorPorId = useMemo(() => Object.fromEntries(colaboradores.map((c) => [c.id, c])), [colaboradores]);

  const horas = num(estado.horas);
  const impostosPct = num(estado.impostosPct);

  const linhasSquad = useMemo(() => estado.linhas.map((l) => {
    const c = l.colaboradorId != null ? colaboradorPorId[l.colaboradorId] : null;
    const custoTotal = c ? num(c.meses_custo_total?.[mes] ?? c.meses_custo_total?.[String(mes)]) : 0;
    return { ...l, colaborador: c, custoTotal, custoHora: horas > 0 ? custoTotal / horas : 0 };
  }), [estado.linhas, colaboradorPorId, mes, horas]);

  const custoMaoDeObra = linhasSquad.reduce((acc, l) => acc + l.custoTotal, 0);
  const qtdNoSquad = linhasSquad.filter((l) => l.colaborador).length;
  const idsNoSquad = new Set(linhasSquad.map((l) => l.colaboradorId).filter((id) => id != null));

  // ── 1. Margem Bruta por Faturamento e Custo ──────────────────────────────
  const pf = estado.porFaturamento;
  const s1Nf = num(pf.valorNf);
  const s1Deducao = s1Nf * (impostosPct / 100);
  const s1ReceitaLiquida = s1Nf - s1Deducao;
  const s1CustoTotal = custoMaoDeObra + num(pf.outros) + num(pf.viagem) + num(pf.freelancer) + num(pf.rateios);
  const s1LucroBruto = s1ReceitaLiquida - s1CustoTotal;
  const s1Margem = s1ReceitaLiquida > 0 ? (s1LucroBruto / s1ReceitaLiquida) * 100 : 0;

  // ── 2. Meta Faturamento por Custo e MB ───────────────────────────────────
  // Receita Líquida = Custo ÷ (1 − MB); NF = Receita Líquida ÷ (1 − impostos).
  const mf = estado.metaFaturamento;
  const s2Mb = num(mf.mbPct) / 100;
  const s2CustoTotal = custoMaoDeObra + num(mf.outros) + num(mf.viagem) + num(mf.freelancer) + num(mf.rateios);
  const s2Valido = s2Mb < 1 && impostosPct < 100;
  const s2ReceitaLiquida = s2Valido ? s2CustoTotal / (1 - s2Mb) : 0;
  const s2Nf = s2Valido ? s2ReceitaLiquida / (1 - impostosPct / 100) : 0;
  const s2Deducao = s2Nf - s2ReceitaLiquida;
  const s2LucroBruto = s2ReceitaLiquida - s2CustoTotal;

  // ── 3. Custo Desejado por Faturamento e MB ───────────────────────────────
  const cd = estado.custoDesejado;
  const s3Nf = num(cd.valorNf);
  const s3Deducao = s3Nf * (impostosPct / 100);
  const s3ReceitaLiquida = s3Nf - s3Deducao;
  const s3LucroBruto = s3ReceitaLiquida * (num(cd.mbPct) / 100);
  const s3Custo = s3ReceitaLiquida - s3LucroBruto;
  const s3Folga = s3Custo - custoMaoDeObra;

  function atualizar(chave, campo, valor) {
    setEstado((e) => ({ ...e, [chave]: { ...e[chave], [campo]: valor } }));
  }

  function adicionarLinha() {
    setEstado((e) => ({
      ...e,
      linhas: [...e.linhas, { uid: Math.max(0, ...e.linhas.map((l) => l.uid)) + 1, colaboradorId: null }],
    }));
  }

  function removerLinha(uid) {
    setEstado((e) => {
      const restantes = e.linhas.filter((l) => l.uid !== uid);
      return { ...e, linhas: restantes.length ? restantes : [{ uid: 1, colaboradorId: null }] };
    });
  }

  function trocarColaborador(uid, valor) {
    const id = valor === "" ? null : Number(valor);
    setEstado((e) => ({ ...e, linhas: e.linhas.map((l) => (l.uid === uid ? { ...l, colaboradorId: id } : l)) }));
  }

  function limparSimulacao() {
    if (!window.confirm("Limpar o squad e todos os valores da simulação?")) return;
    setEstado(ESTADO_INICIAL);
  }

  const thStyle = {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.text, background: C.destaqueBg,
    textAlign: "left", whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}`,
  };
  const tdStyle = { padding: "6px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, color: C.text };
  const selectHeaderStyle = {
    background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8,
    padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600,
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Simulação de Squads</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={mes} onChange={(e) => setMes(parseInt(e.target.value, 10))} style={selectHeaderStyle} title="Mês de referência do custo dos colaboradores">
              {MESES_ABREV.map((m, i) => <option key={m} value={i + 1} style={{ color: "#0F172A" }}>{m}</option>)}
            </select>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))} style={selectHeaderStyle}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={limparSimulacao} style={headerBtnStyle}>
              <RotateCcw size={14} /> Limpar
            </button>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>
              Simulações para definição de metas de margem bruta, faturamento ou custo total
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Monte o squad com colaboradores da Mão de Obra (Operação): o custo de cada um é o custo total
              (salário + encargos + benefícios) de {MESES_ABREV[mes - 1]}/{ano}. Os campos com borda são editáveis.
              A simulação não é gravada no orçamento — fica salva só neste navegador.
            </p>
          </div>

          {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(460px, 1.1fr) minmax(0, 2fr)", gap: 20, alignItems: "start" }}>
            {/* ── Squad ── */}
            <section style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden",
              boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
            }}>
              <div style={{ padding: "14px 14px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Users size={15} color={C.brand} />
                  <h2 style={{ margin: 0, fontSize: 13.5, fontWeight: 800, color: C.text }}>Squad</h2>
                  <span style={{ fontSize: 11, color: C.muted }}>{qtdNoSquad} colaborador(es)</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>Horas/mês</span>
                  <CampoNumero C={C} valor={estado.horas} largura={80} onChange={(v) => setEstado((e) => ({ ...e, horas: v }))} />
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 70 }}>Tipo</th>
                      <th style={thStyle}>Nome Colaborador</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Custo Total</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Custo/Hora</th>
                      <th style={{ ...thStyle, width: 32 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {linhasSquad.map((l) => (
                      <tr key={l.uid}>
                        <td style={{ ...tdStyle, color: C.muted, fontSize: 11.5, fontWeight: 600 }}>
                          {l.colaborador?.regime_atual || "—"}
                        </td>
                        <td style={tdStyle}>
                          <select
                            value={l.colaboradorId ?? ""}
                            onChange={(e) => trocarColaborador(l.uid, e.target.value)}
                            disabled={carregando}
                            style={{
                              width: "100%", minWidth: 200, padding: "5px 8px", borderRadius: 6, fontSize: 12.5,
                              border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontFamily: "inherit",
                            }}
                          >
                            <option value="">{carregando ? "Carregando…" : "Selecione…"}</option>
                            {l.colaboradorId != null && !l.colaborador && !carregando && (
                              <option value={l.colaboradorId}>Colaborador fora de {ano}</option>
                            )}
                            {colaboradores.map((c) => (
                              <option key={c.id} value={c.id} disabled={idsNoSquad.has(c.id) && c.id !== l.colaboradorId}>
                                {rotuloColaborador(c)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {formatCurrency(l.custoTotal)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {formatCurrency(l.custoHora)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button
                            onClick={() => removerLinha(l.uid)}
                            title="Remover do squad"
                            style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 2, display: "flex" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ ...tdStyle, fontWeight: 800, background: C.linhaBg, borderBottom: "none" }}>Total do squad</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 800, background: C.linhaBg, borderBottom: "none", whiteSpace: "nowrap" }}>
                        {formatCurrency(custoMaoDeObra)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 800, background: C.linhaBg, borderBottom: "none", whiteSpace: "nowrap" }}>
                        {formatCurrency(horas > 0 ? custoMaoDeObra / horas : 0)}
                      </td>
                      <td style={{ ...tdStyle, background: C.linhaBg, borderBottom: "none" }} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <button
                  onClick={adicionarLinha}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, background: C.brand, color: "#fff", border: "none",
                    borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  <Plus size={14} /> Adicionar colaborador
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>Impostos</span>
                  <CampoNumero C={C} valor={estado.impostosPct} sufixo="%" largura={90} onChange={(v) => setEstado((e) => ({ ...e, impostosPct: v }))} />
                </div>
              </div>
            </section>

            {/* ── Simuladores ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, alignItems: "start" }}>
              <QuadroSimulacao C={C} isLight={isLight} icon={TrendingUp} titulo="Margem Bruta por Faturamento e Custo"
                subtitulo="Informe a nota fiscal e os custos — veja a margem bruta resultante.">
                <Linha C={C} label="Valor Nota Fiscal" negrito faixa
                  input={<CampoNumero C={C} valor={pf.valorNf} prefixo="R$" onChange={(v) => atualizar("porFaturamento", "valorNf", v)} />} />
                <Linha C={C} label="Dedução" valor={formatCurrency(s1Deducao)} />
                <Linha C={C} label="Impostos" valor={`${impostosPct.toLocaleString("pt-BR")}%`} />
                <Linha C={C} label="Receita Líquida" valor={formatCurrency(s1ReceitaLiquida)} negrito faixa />
                <Linha C={C} label="Custo total" valor={formatCurrency(s1CustoTotal)} negrito faixa />
                <Linha C={C} label="Custo Mão de Obra" valor={formatCurrency(custoMaoDeObra)} sutil />
                <Linha C={C} label="Outros Custos" sutil input={<CampoNumero C={C} valor={pf.outros} prefixo="R$" onChange={(v) => atualizar("porFaturamento", "outros", v)} />} />
                <Linha C={C} label="Viagem" sutil input={<CampoNumero C={C} valor={pf.viagem} prefixo="R$" onChange={(v) => atualizar("porFaturamento", "viagem", v)} />} />
                <Linha C={C} label="Freelancer" sutil input={<CampoNumero C={C} valor={pf.freelancer} prefixo="R$" onChange={(v) => atualizar("porFaturamento", "freelancer", v)} />} />
                <Linha C={C} label="Demais (rateios)" sutil input={<CampoNumero C={C} valor={pf.rateios} prefixo="R$" onChange={(v) => atualizar("porFaturamento", "rateios", v)} />} />
                <Linha C={C} label="Lucro Bruto" valor={formatCurrency(s1LucroBruto)} negrito corValor={s1LucroBruto < 0 ? C.danger : undefined} />
                <Linha C={C} label="Margem Bruta" destaque
                  valor={`${s1Margem.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                  corValor={s1LucroBruto < 0 ? C.danger : undefined} />
              </QuadroSimulacao>

              <QuadroSimulacao C={C} isLight={isLight} icon={Target} titulo="Meta Faturamento por Custo e MB"
                subtitulo="Informe a margem desejada e os custos — veja quanto precisa faturar.">
                <Linha C={C} label="Margem Bruta Desejada" negrito faixa
                  input={<CampoNumero C={C} valor={mf.mbPct} sufixo="%" largura={90} onChange={(v) => atualizar("metaFaturamento", "mbPct", v)} />} />
                <Linha C={C} label="Dedução" valor={formatCurrency(s2Deducao)} />
                <Linha C={C} label="Impostos" valor={`${impostosPct.toLocaleString("pt-BR")}%`} />
                <Linha C={C} label="Receita Líquida" valor={formatCurrency(s2ReceitaLiquida)} negrito faixa />
                <Linha C={C} label="Custo total" valor={formatCurrency(s2CustoTotal)} negrito faixa />
                <Linha C={C} label="Custo Mão de Obra" valor={formatCurrency(custoMaoDeObra)} sutil />
                <Linha C={C} label="Outros Custos" sutil input={<CampoNumero C={C} valor={mf.outros} prefixo="R$" onChange={(v) => atualizar("metaFaturamento", "outros", v)} />} />
                <Linha C={C} label="Viagem" sutil input={<CampoNumero C={C} valor={mf.viagem} prefixo="R$" onChange={(v) => atualizar("metaFaturamento", "viagem", v)} />} />
                <Linha C={C} label="Freelancer" sutil input={<CampoNumero C={C} valor={mf.freelancer} prefixo="R$" onChange={(v) => atualizar("metaFaturamento", "freelancer", v)} />} />
                <Linha C={C} label="Demais (rateios)" sutil input={<CampoNumero C={C} valor={mf.rateios} prefixo="R$" onChange={(v) => atualizar("metaFaturamento", "rateios", v)} />} />
                <Linha C={C} label="Lucro Bruto" valor={formatCurrency(s2LucroBruto)} negrito />
                <Linha C={C} label="Meta Faturamento" destaque valor={s2Valido ? formatCurrency(s2Nf) : "MB deve ser < 100%"} />
              </QuadroSimulacao>

              <QuadroSimulacao C={C} isLight={isLight} icon={Wallet} titulo="Custo Desejado por Faturamento e MB"
                subtitulo="Informe a nota fiscal e a margem desejada — veja o custo máximo do squad.">
                <Linha C={C} label="Valor Nota Fiscal" negrito faixa
                  input={<CampoNumero C={C} valor={cd.valorNf} prefixo="R$" onChange={(v) => atualizar("custoDesejado", "valorNf", v)} />} />
                <Linha C={C} label="Dedução" valor={formatCurrency(s3Deducao)} />
                <Linha C={C} label="Impostos" valor={`${impostosPct.toLocaleString("pt-BR")}%`} />
                <Linha C={C} label="Receita Líquida" valor={formatCurrency(s3ReceitaLiquida)} negrito faixa />
                <Linha C={C} label="Lucro Bruto" valor={formatCurrency(s3LucroBruto)} negrito faixa />
                <Linha C={C} label="Margem Bruta Desejada" negrito
                  input={<CampoNumero C={C} valor={cd.mbPct} sufixo="%" largura={90} onChange={(v) => atualizar("custoDesejado", "mbPct", v)} />} />
                <Linha C={C} label="Custo (Total)" destaque valor={formatCurrency(s3Custo)} />
                {s3Nf > 0 && (
                  <Linha C={C} sutil label={s3Folga >= 0 ? "Sobra após mão de obra do squad" : "Mão de obra do squad excede em"}
                    valor={formatCurrency(Math.abs(s3Folga))} corValor={s3Folga >= 0 ? C.success : C.danger} />
                )}
              </QuadroSimulacao>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
